import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Loader2,
  AlertCircle,
  ChevronRight,
  MessageSquare,
} from 'lucide-react';
import PageTitle from '@/components/PageTitle';
import { useMeteringThreadDetail, useMeteringThreadWarehouse } from '@/services/metering.store';
import { useOpenConversation } from '@/hooks/useOpenConversation';
import { ConversationModeSwitchDialog } from '@/components/ConversationModeSwitchDialog';

const fmtUsd = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  }).format(n);

const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(n);

const fmtCompact = (n: number) =>
  new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n);

const fmtDuration = (ms: number) => {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const fmtTimestamp = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString() : '—';

function StatCard({
  label,
  value,
  hint,
  isLoading,
}: {
  label: string;
  value: string;
  hint?: string;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <>
            <div className="text-2xl font-semibold">{value}</div>
            {hint ? <div className="text-xs text-muted-foreground mt-1">{hint}</div> : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

type TimelinePoint = {
  x: number;
  y: number;
  cost: number;
  llmCost: number;
  warehouseCost: number;
  turnIndex: number;
  createdAt: string;
  model: string;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
};

const POINT_RANGE: [number, number] = [60, 1400];

const LLM_COLOR = 'hsl(var(--chart-1))';
const WAREHOUSE_COLOR = 'hsl(var(--chart-2))';

/** SVG path for a pie slice spanning [startFraction, endFraction) of the unit circle,
 *  starting at 12 o'clock and going clockwise. Used to draw each turn's bubble as
 *  two slices (LLM portion + warehouse portion). */
function arcSlicePath(
  cx: number,
  cy: number,
  r: number,
  startFraction: number,
  endFraction: number,
): string {
  const span = endFraction - startFraction;
  if (span <= 0) return '';
  if (span >= 1) {
    // Full circle — draw via two half-arcs because a single 360° arc collapses.
    return `M ${cx},${cy - r} A ${r},${r} 0 1 1 ${cx},${cy + r} A ${r},${r} 0 1 1 ${cx},${cy - r} Z`;
  }
  const a0 = -Math.PI / 2 + startFraction * 2 * Math.PI;
  const a1 = -Math.PI / 2 + endFraction * 2 * Math.PI;
  const x0 = cx + r * Math.cos(a0);
  const y0 = cy + r * Math.sin(a0);
  const x1 = cx + r * Math.cos(a1);
  const y1 = cy + r * Math.sin(a1);
  const largeArc = span > 0.5 ? 1 : 0;
  return `M ${cx},${cy} L ${x0},${y0} A ${r},${r} 0 ${largeArc} 1 ${x1},${y1} Z`;
}

function SplitBubble(props: any) {
  const { cx, cy, payload } = props;
  if (typeof cx !== 'number' || typeof cy !== 'number') return null;
  // recharts passes the z-derived area in px² as either `size` (recent versions)
  // or `node.size` (older). Fall back to the min of POINT_RANGE so a missing
  // value still renders something visible instead of a zero-radius dot.
  const area = Math.max(props.size ?? props.node?.size ?? 0, POINT_RANGE[0]);
  const r = Math.sqrt(area / Math.PI);
  const p: TimelinePoint = payload;
  const llmFraction = p.cost > 0 ? p.llmCost / p.cost : 1;
  return (
    <g>
      <path d={arcSlicePath(cx, cy, r, 0, llmFraction)} fill={LLM_COLOR} fillOpacity={0.7} />
      <path d={arcSlicePath(cx, cy, r, llmFraction, 1)} fill={WAREHOUSE_COLOR} fillOpacity={0.7} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="hsl(var(--border))" strokeWidth={0.75} />
    </g>
  );
}

export interface ThreadUsageDetailProps {
  threadId: string | undefined;
}

/**
 * Per-thread LLM + warehouse cost breakdown.
 *
 * Rendered two ways: inside the extra-large ThreadUsageModal (over whatever the
 * user was looking at), and as a standalone page for deep links to
 * /usage-analytics/ellie-usage/threads/:thread_id. The modal brings its own
 * close button, so there's no back/close control here.
 */
export function ThreadUsageDetail({ threadId }: ThreadUsageDetailProps) {
  const thread_id = threadId;
  const { data, isLoading, isError } = useMeteringThreadDetail(thread_id);
  // Opening the thread may require flipping the app's mode: conversations live
  // in branch-scoped collections, so a PROD thread is invisible from Develop
  // mode (and vice versa). The hook asks before switching.
  const { open: openConversation, pending, confirm, cancel, switching } = useOpenConversation();
  // Warehouse data is fetched separately because the Snowflake query is slow
  // (1–4s) — by splitting it out the LLM portion of the page renders right
  // away while the warehouse section spins on its own.
  const {
    data: warehouseData,
    isLoading: warehouseLoading,
  } = useMeteringThreadWarehouse(thread_id);

  // Convenience: combine LLM and warehouse totals once both halves arrive.
  // Falls back to just LLM cost while warehouse is still loading so the
  // "Total cost" stat shows a number immediately instead of an em-dash.
  const totalCostUsd =
    (data?.llm_cost_usd ?? 0) + (warehouseData?.warehouse_cost_usd ?? 0);

  const cacheHitRate =
    data && (data.cache_read_input_tokens + data.cache_creation_input_tokens) > 0
      ? data.cache_read_input_tokens /
        (data.cache_read_input_tokens + data.cache_creation_input_tokens)
      : 0;

  // Bucket warehouse queries into the turn they belong to: each query attributes
  // to the most recent assistant turn that started before it (the agent emitted
  // a tool_use, the warehouse query ran, then the next assistant turn ingested
  // the tool_result). Last turn catches everything after it.
  const timeline: TimelinePoint[] = useMemo(() => {
    if (!data || data.turns.length === 0) return [];
    const turnStarts = data.turns.map((t) => new Date(t.created_at).getTime());
    const warehouseByTurn: number[] = data.turns.map(() => 0);
    // Warehouse queries come from the warehouse endpoint; the timeline starts
    // out LLM-only and the per-turn warehouse split fills in once that loads.
    for (const q of warehouseData?.warehouse_queries ?? []) {
      if (!q.start_time) continue;
      const t = new Date(q.start_time).getTime();
      let idx = 0;
      for (let i = turnStarts.length - 1; i >= 0; i--) {
        if (turnStarts[i] <= t) {
          idx = i;
          break;
        }
      }
      warehouseByTurn[idx] += q.cost_usd;
    }
    return data.turns.map((t, i) => ({
      x: new Date(t.created_at).getTime(),
      y: 1,
      cost: t.total_cost_usd + warehouseByTurn[i],
      llmCost: t.total_cost_usd,
      warehouseCost: warehouseByTurn[i],
      turnIndex: t.turn_index,
      createdAt: t.created_at,
      model: t.model_name,
      totalTokens: t.total_tokens,
      inputTokens: t.input_tokens,
      outputTokens: t.output_tokens,
      cacheReadTokens: t.cache_read_input_tokens,
    }));
  }, [data, warehouseData]);

  const timelineDomain: [number, number] | undefined = useMemo(() => {
    if (timeline.length === 0) return undefined;
    const xs = timeline.map((p) => p.x);
    let lo = Math.min(...xs);
    let hi = Math.max(...xs);
    if (lo === hi) {
      // Single-point — pad so the dot doesn't sit on the axis edges.
      const oneMin = 60_000;
      lo -= oneMin;
      hi += oneMin;
    } else {
      const span = hi - lo;
      const pad = span * 0.05;
      lo -= pad;
      hi += pad;
    }
    return [lo, hi];
  }, [timeline]);

  const timelineZDomain: [number, number] | undefined = useMemo(() => {
    if (timeline.length === 0) return undefined;
    const costs = timeline.map((p) => p.cost);
    return [0, Math.max(...costs)];
  }, [timeline]);

  const renderTimelineTooltip = ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    const p: TimelinePoint = payload[0].payload;
    return (
      <div className="rounded-md border bg-popover p-3 text-popover-foreground shadow-md text-xs space-y-1">
        <div className="font-semibold">Turn {p.turnIndex} · {p.model}</div>
        <div className="text-muted-foreground">{fmtTimestamp(p.createdAt)}</div>
        <div className="pt-1 grid grid-cols-2 gap-x-3 gap-y-0.5">
          <span className="text-muted-foreground">Total cost</span>
          <span className="text-right font-medium">{fmtUsd(p.cost)}</span>
          <span className="text-muted-foreground">LLM</span>
          <span className="text-right">{fmtUsd(p.llmCost)}</span>
          <span className="text-muted-foreground">Warehouse</span>
          <span className="text-right">{fmtUsd(p.warehouseCost)}</span>
          <span className="text-muted-foreground">Tokens</span>
          <span className="text-right">{fmtCompact(p.totalTokens)}</span>
          <span className="text-muted-foreground">Input / output</span>
          <span className="text-right">{fmtCompact(p.inputTokens)} / {fmtCompact(p.outputTokens)}</span>
          {p.cacheReadTokens > 0 ? (
            <>
              <span className="text-muted-foreground">Cache read</span>
              <span className="text-right">{fmtCompact(p.cacheReadTokens)}</span>
            </>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="p-8 h-full overflow-auto">
      <div className="mb-6">
        {/* pr-10 keeps the title clear of the modal's close button. */}
        <div className="pr-10">
          <PageTitle title={data?.thread_name || 'Thread details'} />
        </div>
        <div className="text-sm text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-mono">{thread_id}</span>
          {data?.agent_type ? (
            <>
              <span>·</span>
              <Badge variant="outline">{data.agent_type}</Badge>
            </>
          ) : null}
          {data?.branch ? (
            <>
              <span>·</span>
              <Badge variant="outline">{data.branch}</Badge>
            </>
          ) : null}
          {data?.actor_label || data?.actor_id ? (
            <>
              <span>·</span>
              <span>
                User: <strong>{data?.actor_label || data?.actor_id}</strong>
              </span>
            </>
          ) : null}
          {data?.created_at ? (
            <>
              <span>·</span>
              <span title={data.created_at}>Created {fmtTimestamp(data.created_at)}</span>
            </>
          ) : null}
          <button
            type="button"
            onClick={() => openConversation(thread_id!, {
              branch: data?.branch,
              agentType: data?.agent_type,
            })}
            className="ml-auto flex items-center gap-1 hover:text-foreground"
            title="Open the chat thread"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            Open chat thread
          </button>
        </div>
      </div>

      {isError ? (
        <div className="border rounded-md p-4 mb-6 text-sm flex items-center gap-2 text-destructive">
          <AlertCircle className="h-4 w-4" />
          Failed to load thread details.
        </div>
      ) : null}

      {data?.unrated_models && data.unrated_models.length > 0 ? (
        <div className="border rounded-md p-3 mb-4 text-sm flex items-start gap-2 bg-yellow-50 dark:bg-yellow-950/30 border-yellow-300 dark:border-yellow-900 text-yellow-900 dark:text-yellow-200">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium">
              No rate card for {data.unrated_models.map((m, i) => (
                <span key={m}>
                  {i > 0 ? ', ' : ''}<code className="font-mono text-xs">{m}</code>
                </span>
              ))} — those turns are pricing as $0.
            </div>
            <div className="mt-1">
              Add to <code className="font-mono text-xs">LATEST_RATE_CARDS</code> and run{' '}
              <code className="font-mono text-xs">apply_latest_rate_cards()</code>.
            </div>
          </div>
        </div>
      ) : null}

      {warehouseData?.warehouse_unavailable ? (
        <div className="border rounded-md p-3 mb-6 text-sm flex items-start gap-2 bg-yellow-50 dark:bg-yellow-950/30 border-yellow-300 dark:border-yellow-900 text-yellow-900 dark:text-yellow-200">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            Warehouse data unavailable for this thread —{' '}
            <code className="font-mono text-xs">SNOWFLAKE.ACCOUNT_USAGE</code> not granted. LLM
            numbers below are still accurate.
          </span>
        </div>
      ) : null}

      <Card className="mb-4">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">Cost timeline</CardTitle>
              <CardDescription>
                Each circle is one assistant turn — sized by total cost. The slice split shows the LLM vs. warehouse share. Hover for details.
              </CardDescription>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground whitespace-nowrap">
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: LLM_COLOR, opacity: 0.7 }}
                />
                LLM
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: WAREHOUSE_COLOR, opacity: 0.7 }}
                />
                Warehouse
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : timeline.length === 0 ? (
            <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">
              No turns recorded yet.
            </div>
          ) : (
            <div className="h-32 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 10, right: 16, bottom: 10, left: 16 }}>
                  <XAxis
                    type="number"
                    dataKey="x"
                    domain={timelineDomain ?? ['auto', 'auto']}
                    tickFormatter={(v: number) => new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    stroke="currentColor"
                    className="text-muted-foreground"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis type="number" dataKey="y" domain={[0, 2]} hide />
                  <ZAxis type="number" dataKey="cost" domain={timelineZDomain ?? ['auto', 'auto']} range={POINT_RANGE} />
                  <RechartsTooltip content={renderTimelineTooltip} cursor={{ strokeDasharray: '3 3' }} />
                  <Scatter data={timeline} shape={<SplitBubble />} />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <StatCard
          label="Total cost"
          value={data ? fmtUsd(totalCostUsd) : '—'}
          hint={
            data
              ? `${fmtUsd(data.llm_cost_usd)} LLM + ${
                  warehouseData
                    ? fmtUsd(warehouseData.warehouse_cost_usd)
                    : warehouseLoading
                      ? '… warehouse'
                      : '$0.00 warehouse'
                }`
              : undefined
          }
          isLoading={isLoading}
        />
        <StatCard
          label="Total tokens"
          value={data ? fmtCompact(data.total_tokens) : '—'}
          hint={
            data
              ? `${fmtCompact(data.input_tokens)} in · ${fmtCompact(
                  data.output_tokens,
                )} out · ${fmtCompact(data.cache_creation_input_tokens)} cache write · ${fmtCompact(
                  data.cache_read_input_tokens,
                )} cache read`
              : undefined
          }
          isLoading={isLoading}
        />
        <StatCard
          label="Messages"
          value={data ? fmtNum(data.message_count) : '—'}
          hint={data ? `${data.models.join(', ')}` : undefined}
          isLoading={isLoading}
        />
        <StatCard
          label="Cache hit rate"
          value={data ? `${Math.round(cacheHitRate * 100)}%` : '—'}
          hint={
            data
              ? `${fmtCompact(data.cache_read_input_tokens)} cached / ${fmtCompact(
                  data.cache_read_input_tokens + data.cache_creation_input_tokens,
                )} total cacheable`
              : undefined
          }
          isLoading={isLoading}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <StatCard
          label="LLM token cost"
          value={data ? fmtUsd(data.llm_cost_usd) : '—'}
          hint={
            data
              ? `${fmtUsd(data.input_cost_usd)} input · ${fmtUsd(
                  data.output_cost_usd,
                )} output · ${fmtUsd(data.cache_creation_cost_usd)} cache write · ${fmtUsd(
                  data.cache_read_cost_usd,
                )} cache read`
              : undefined
          }
          isLoading={isLoading}
        />
        <StatCard
          label="Warehouse compute cost"
          value={warehouseData ? fmtUsd(warehouseData.warehouse_cost_usd) : '—'}
          hint={
            warehouseData
              ? `${fmtNum(warehouseData.warehouse_query_count)} queries · ${warehouseData.warehouse_credits.toFixed(4)} credits`
              : undefined
          }
          isLoading={warehouseLoading}
        />
      </div>

      <h2 className="text-lg font-semibold mb-1">Tools used</h2>
      <p className="text-xs text-muted-foreground mb-3">
        Sorted by total tokens returned — the tool dumping the most data into the conversation
        appears first. Each tool result becomes input on the next assistant turn, so a single
        heavy result gets re-billed every subsequent turn (unless conversation caching is on).
        Token counts are estimates (~4 chars/token).
      </p>
      <div className="border rounded-md mb-8 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Function</TableHead>
              <TableHead className="text-right">Calls</TableHead>
              <TableHead className="text-right">Total tokens (est)</TableHead>
              <TableHead className="text-right">Largest single result</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                  Loading…
                </TableCell>
              </TableRow>
            ) : !data || data.tool_calls.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                  No tool calls in this thread.
                </TableCell>
              </TableRow>
            ) : (
              data.tool_calls.map((t) => (
                <TableRow key={t.name}>
                  <TableCell className="font-mono text-sm">{t.name}</TableCell>
                  <TableCell className="text-right">{fmtNum(t.count)}</TableCell>
                  <TableCell className="text-right">
                    {t.total_result_tokens_est > 0 ? `~${fmtCompact(t.total_result_tokens_est)}` : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    {t.max_result_tokens_est > 0 ? `~${fmtCompact(t.max_result_tokens_est)}` : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {data && data.heaviest_tool_calls && data.heaviest_tool_calls.length > 0 && (
        <>
          <h2 className="text-lg font-semibold mb-1">Heaviest tool calls</h2>
          <p className="text-xs text-muted-foreground mb-3">
            The individual tool invocations that returned the most data — these are usually the
            top cost drivers in a thread. Each row's tokens get added to the conversation context
            and re-billed on every subsequent turn.
          </p>
          <div className="border rounded-md mb-8 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Turn</TableHead>
                  <TableHead>Function</TableHead>
                  <TableHead className="text-right">Result tokens (est)</TableHead>
                  <TableHead className="text-right">Result chars</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.heaviest_tool_calls.map((t, i) => (
                  <TableRow key={t.id || `${t.name}-${i}`}>
                    <TableCell>#{t.turn_index}</TableCell>
                    <TableCell className="font-mono text-sm">{t.name}</TableCell>
                    <TableCell className="text-right">~{fmtCompact(t.result_tokens_est)}</TableCell>
                    <TableCell className="text-right">{fmtNum(t.result_chars)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      <h2 className="text-lg font-semibold mb-3">Warehouse queries</h2>
      <p className="text-xs text-muted-foreground mb-2">
        Sorted by credits descending — the queries at the top are the ones most worth tuning.
        Lags up to 8 hours behind real time.
      </p>
      <div className="border rounded-md mb-8 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Query</TableHead>
              <TableHead>Warehouse</TableHead>
              <TableHead>Started</TableHead>
              <TableHead className="text-right">Duration</TableHead>
              <TableHead className="text-right">Credits</TableHead>
              <TableHead className="text-right">Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {warehouseLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                  Loading warehouse data (Snowflake)…
                </TableCell>
              </TableRow>
            ) : !warehouseData || warehouseData.warehouse_queries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                  {warehouseData?.warehouse_unavailable
                    ? 'Warehouse cost data not accessible — grant ACCOUNT_USAGE access to enable.'
                    : 'No warehouse queries attributed to this thread yet.'}
                </TableCell>
              </TableRow>
            ) : (
              warehouseData.warehouse_queries.map((q) => (
                <TableRow key={q.query_id}>
                  <TableCell className="max-w-md">
                    <div className="font-mono text-xs whitespace-pre-wrap break-words text-muted-foreground line-clamp-3">
                      {q.query_text || q.query_id}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{q.warehouse_name ?? '—'}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm" title={q.start_time ?? ''}>
                    {fmtTimestamp(q.start_time)}
                  </TableCell>
                  <TableCell className="text-right">{fmtDuration(q.elapsed_ms)}</TableCell>
                  <TableCell className="text-right">{q.credits.toFixed(6)}</TableCell>
                  <TableCell className="text-right font-medium">{fmtUsd(q.cost_usd)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <h2 className="text-lg font-semibold mb-3">Per-turn timeline</h2>
      <div className="border rounded-md overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead>Time</TableHead>
              <TableHead>Model</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead className="text-right">Input</TableHead>
              <TableHead className="text-right">Output</TableHead>
              <TableHead className="text-right">Cache write</TableHead>
              <TableHead className="text-right">Cache read</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead>Stop reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-muted-foreground py-6">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                  Loading…
                </TableCell>
              </TableRow>
            ) : !data || data.turns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-muted-foreground py-6">
                  No turns recorded yet.
                </TableCell>
              </TableRow>
            ) : (
              data.turns.map((t) => (
                <TableRow key={t.turn_index}>
                  <TableCell>{t.turn_index}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm" title={t.created_at}>
                    {fmtTimestamp(t.created_at)}
                  </TableCell>
                  <TableCell className="text-sm">
                    <Badge variant="secondary" className="text-[10px]">{t.model_name}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {t.branch ? (
                      <Badge variant="outline" className="text-[10px]">{t.branch}</Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{fmtCompact(t.input_tokens)}</TableCell>
                  <TableCell className="text-right">{fmtCompact(t.output_tokens)}</TableCell>
                  <TableCell className="text-right">{fmtCompact(t.cache_creation_input_tokens)}</TableCell>
                  <TableCell className="text-right">{fmtCompact(t.cache_read_input_tokens)}</TableCell>
                  <TableCell className="text-right font-medium">{fmtUsd(t.total_cost_usd)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t.stop_reason ?? '—'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ConversationModeSwitchDialog
        pending={pending}
        onConfirm={confirm}
        onCancel={cancel}
        switching={switching}
      />
    </div>
  );
}

/**
 * Route entry for /usage-analytics/ellie-usage/threads/:thread_id.
 *
 * In-app clicks open the takeover instead; this exists so deep links and the
 * legacy /metering/llm/threads/:id redirect still resolve to something.
 */
export default function MeteringThreadDetailPage() {
  const { thread_id } = useParams<{ thread_id: string }>();
  return <ThreadUsageDetail threadId={thread_id} />;
}
